import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { DetailsHistorieCardComponent } from '../../../components/details-historie-card/details-historie-card.component';
import { DetailsHistoriePanelDirective } from '../../../components/details-historie-card/details-historie-panel.directive';
import { PersonOption } from '../../../components/person-select/person-select.component';
import { SaveToastService } from '../../../components/save-toast/save-toast.service';
import { UebergabePanelComponent } from '../../../components/uebergabe-panel/uebergabe-panel.component';
import {
  aktiveStufe,
  aktivSeit,
  toUebergabeStufen,
  uebergabeFehlerKey,
  uebergebenGesperrt,
} from '../../../components/uebergabe-panel/uebergabe-kette';
import { VerlaufListeComponent } from '../../../components/verlauf-liste/verlauf-liste.component';
import {
  DetailsHistorieStatus,
  DetailsHistorieTab,
  UebergabeEntwurf,
  UebergabeHandOff,
  VerlaufEintrag,
} from '../../../models/details-historie.model';
import { HandoverChain } from '../../../models/handover-chain.model';
import { TestdatenblattActivity } from '../../../models/testdatenblatt.model';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';

const KEY = 'testdatenblaetter.form.historie.';
const VORLAGEN = ['zeilen', 'werte', 'reviewen'] as const;
// Der gemeinsame Text passt grammatisch nicht zum Neutrum „Testdatenblatt“.
const BEREITS_WEITERGEGEBEN = 'common.detailsHistorie.uebergabe.fehler.bereitsWeitergegeben';

@Component({
  selector: 'app-testdatenblatt-details-historie',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DetailsHistorieCardComponent,
    DetailsHistoriePanelDirective,
    UebergabePanelComponent,
    VerlaufListeComponent,
  ],
  templateUrl: './testdatenblatt-details-historie.component.html',
  styleUrl: './testdatenblatt-details-historie.component.scss',
})
export class TestdatenblattDetailsHistorieComponent {
  readonly projectId = input.required<string>();
  readonly testdatenblattId = input<string | null>(null);
  readonly readonly = input<boolean>(false);
  readonly personOptions = input<PersonOption[]>([]);
  readonly currentUserId = input<string | null>(null);
  readonly notes = input<string>('');
  readonly notesControl = input.required<FormControl<string>>();

  protected readonly maxNotes = FIELD_LENGTHS.FREETEXT_MAX;

  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly service = inject(TestdatenblattService);
  private readonly toast = inject(SaveToastService);
  private readonly sprache = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly aktiverReiter = signal('');
  // Hält den Übergabe-Entwurf, weil der Reiterwechsel das Panel zerstört.
  readonly entwurf = signal<UebergabeEntwurf | null>(null);

  protected readonly kette = signal<HandoverChain | null>(null);
  protected readonly ketteStatus = signal<DetailsHistorieStatus>('bereit');
  protected readonly beschaeftigt = signal(false);
  protected readonly aktionsFehler = signal<string | null>(null);
  protected readonly verlaufStatus = signal<DetailsHistorieStatus>('bereit');
  private readonly aktivitaeten = signal<TestdatenblattActivity[]>([]);
  private ketteAbruf: Subscription | null = null;
  private verlaufAbruf: Subscription | null = null;

  protected readonly istBearbeiten = computed(() => !!this.testdatenblattId());
  protected readonly stufen = computed(() => toUebergabeStufen(this.kette()));
  protected readonly seit = computed(() => aktivSeit(this.kette()));
  private readonly inhaberId = computed(() => aktiveStufe(this.kette())?.userId ?? null);

  protected readonly auswahl = computed(() => {
    const ausgeschlossen = new Set([this.inhaberId(), this.currentUserId()]);
    return this.personOptions().filter((person) => !ausgeschlossen.has(person.id));
  });

  protected readonly sperrGrund = computed<string | null>(() => {
    this.sprache();
    return this.istBearbeiten() ? null : this.translate.instant(`${KEY}erstNachAnlegen`);
  });

  protected readonly uebergebenSperrGrund = computed<string | null>(() => {
    this.sprache();
    return uebergebenGesperrt(this.kette(), this.inhaberId()) ? this.fehlerText(BEREITS_WEITERGEGEBEN) : null;
  });

  protected readonly vorlagen = computed<string[]>(() => {
    this.sprache();
    return VORLAGEN.map((key) => this.translate.instant(`${KEY}vorlagen.${key}`));
  });

  protected readonly verlaufEintraege = computed<VerlaufEintrag[]>(() => {
    this.sprache();
    return this.aktivitaeten().map((aktivitaet) => ({
      personName: aktivitaet.actorName ?? '',
      ereignis: this.translate.instant(`${KEY}typen.${aktivitaet.type}`),
      detail: aktivitaet.detail,
      zeit: aktivitaet.createdAt,
    }));
  });

  protected readonly reiter = computed<DetailsHistorieTab[]>(() => {
    this.sprache();
    const label = (id: string): string => this.translate.instant(`${KEY}reiter.${id}`);
    return [
      { id: 'uebergabe', label: label('uebergabe'), count: this.kette()?.stages.length || null },
      { id: 'verlauf', label: label('verlauf'), count: this.aktivitaeten().length || null },
      { id: 'notizen', label: label('notizen'), count: this.notes().trim() ? 1 : null },
    ];
  });

  constructor() {
    effect(() => {
      const id = this.testdatenblattId();
      untracked(() => {
        this.kette.set(null);
        this.entwurf.set(null);
        this.aktionsFehler.set(null);
        this.aktivitaeten.set([]);
        if (!id) return;
        this.ladeKette();
        this.ladeVerlauf();
      });
    });
  }

  ladeKette(): void {
    const id = this.testdatenblattId();
    if (!id) return;
    this.ketteAbruf?.unsubscribe();
    this.ketteStatus.set('laedt');
    this.ketteAbruf = this.service
      .getHandovers(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          this.kette.set(kette);
          this.ketteStatus.set('bereit');
        },
        error: () => this.ketteStatus.set('fehler'),
      });
  }

  ladeVerlauf(): void {
    const id = this.testdatenblattId();
    if (!id) return;
    this.verlaufAbruf?.unsubscribe();
    this.verlaufStatus.set('laedt');
    this.verlaufAbruf = this.service
      .getActivity(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (aktivitaeten) => {
          this.aktivitaeten.set(aktivitaeten);
          this.verlaufStatus.set('bereit');
        },
        error: () => this.verlaufStatus.set('fehler'),
      });
  }

  protected uebergeben(handOff: UebergabeHandOff): void {
    const name = this.personOptions().find((person) => person.id === handOff.userId)?.displayName ?? '';
    this.fuehreAus(
      (projectId, id) =>
        this.service.handOff(projectId, id, {
          userId: handOff.userId,
          message: handOff.message ?? '',
          notify: handOff.notify,
        }),
      () => this.toast.show(this.translate.instant(`${KEY}uebergebenAn`, { name })),
    );
  }

  protected entwurfGeaendert(entwurf: UebergabeEntwurf | null): void {
    this.entwurf.set(entwurf);
    if (!entwurf) this.aktionsFehler.set(null);
  }

  protected abschliessen(): void {
    this.fuehreAus((projectId, id) => this.service.closeHandover(projectId, id));
  }

  protected wiederOeffnen(): void {
    this.fuehreAus((projectId, id) => this.service.reopenHandover(projectId, id));
  }

  protected ueberspringen(stageId: string): void {
    this.fuehreAus((projectId, id) => this.service.skipHandover(projectId, id, stageId));
  }

  private fuehreAus(
    aufruf: (projectId: string, testdatenblattId: string) => Observable<HandoverChain>,
    danach?: () => void,
  ): void {
    const id = this.testdatenblattId();
    if (!id || this.readonly() || this.beschaeftigt()) return;
    this.beschaeftigt.set(true);
    aufruf(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          this.kette.set(kette);
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(null);
          danach?.();
          this.ladeVerlauf();
        },
        error: (err: HttpErrorResponse) => {
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(this.fehlerText(uebergabeFehlerKey(err)));
          // Sonst hielte das Panel eine später geladene fremde Stufe für das eigene Ergebnis und leerte den Entwurf.
          this.entwurf.update((entwurf) => entwurf && { ...entwurf, stufenBeimAbsenden: null });
        },
      });
  }

  private fehlerText(key: string): string {
    return this.translate.instant(key === BEREITS_WEITERGEGEBEN ? `${KEY}bereitsWeitergegeben` : key);
  }
}
